import { useRef, useState, type ChangeEvent } from 'react';
import { useUser } from '@clerk/clerk-react';
import { api } from '@aeon-karaoke/cloud-convex';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';
import type { CurrentUser } from '@/hook/useCurrentUser';
import { cn } from '@/lib/cn';
import { AvatarDisc } from './AvatarDisc';
import { AvatarPickerModal } from './AvatarPickerModal';
import { Button } from './Button';
import { MonoLabel } from './MonoLabel';

interface ProfilePictureProps {
  user: CurrentUser;
  /** First run uses the 56px inline row; Settings the 140px stacked column. */
  layout: 'row' | 'column';
}

/**
 * Avatar with Upload and Pick an avatar. Uploads go to Clerk (the webhook
 * syncs imageUrl) and clear any catalog pick; a pick sets avatarKey, which
 * AvatarDisc shows ahead of the Clerk image.
 */
export function ProfilePicture({ user, layout }: ProfilePictureProps) {
  const { user: clerkUser } = useUser();
  const fileInput = useRef<HTMLInputElement>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !clerkUser) return;
    setBusy(true);
    setError(null);
    const result = await toResult(async () => {
      await clerkUser.setProfileImage({ file });
      await convex.mutation(api.users.updateProfile, { avatarKey: null });
      return null;
    });
    setBusy(false);
    if (!result.ok) setError("We couldn't upload that picture.");
  }

  async function pick(key: string) {
    setPicking(false);
    setBusy(true);
    setError(null);
    const result = await toResult(() => convex.mutation(api.users.updateProfile, { avatarKey: key }));
    setBusy(false);
    if (!result.ok) setError(result.error.message);
  }

  const buttons = (
    <>
      <Button variant="outline" size="sm" disabled={busy} onClick={() => fileInput.current?.click()}>
        Upload
      </Button>
      <Button variant="outline" size="sm" quiet disabled={busy} onClick={() => setPicking(true)}>
        Pick an avatar
      </Button>
      <input ref={fileInput} type="file" accept="image/*" onChange={(event) => void upload(event)} className="hidden" />
    </>
  );

  return (
    <div>
      <MonoLabel tone="mute" className="block">
        {layout === 'row' ? 'Picture' : 'Profile picture'}
      </MonoLabel>
      {layout === 'row' ? (
        <div className="mt-[10px] flex items-center gap-[14px]">
          <AvatarDisc user={user} size={56} />
          {buttons}
        </div>
      ) : (
        <>
          <AvatarDisc user={user} size={140} className="mt-[14px]" />
          <div className="mt-4 flex flex-wrap gap-2">{buttons}</div>
        </>
      )}
      {error && <p className={cn('text-[13px] text-down', layout === 'row' ? 'mt-2' : 'mt-3')}>{error}</p>}
      <AvatarPickerModal open={picking} onClose={() => setPicking(false)} current={user.avatarKey} onPick={pick} />
    </div>
  );
}
